import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

// Icônes SVG pour le menu mobile
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

// Logo image
const LogoImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }
  
  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

// Composants stylisés
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'Arial', sans-serif;
`;

// Navbar
const Navbar = styled.nav`
  background: linear-gradient(135deg, #0a192f 0%, #172a45 50%, #263b5c 100%);
  padding: 0.75rem 2rem;
  box-shadow: 0 2px 15px rgba(0,0,0,0.15);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  backdrop-filter: blur(15px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;



const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    position: relative;
  }
`;

const Logo = styled.div`
  color: white;
  font-size: 1.6rem;
  font-weight: 700;
  font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: -0.02em;
  
  &:hover {
    transform: scale(1.02);
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const NavButtons = styled.div<{ isOpen: boolean }>`
  display: flex;
  gap: 12px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 8px;
    position: fixed;
    top: 80px;
    right: 20px;
    left: auto;
    width: 280px;
    max-width: calc(100vw - 40px);
    background: linear-gradient(135deg, #0a192f 0%, #172a45 100%);
    backdrop-filter: blur(20px);
    flex-direction: column;
    padding: 16px;
    border-radius: 24px;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(120%)'};
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 10px 40px rgba(0,0,0,0.4);
    z-index: 1100;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const NavButton = styled.button<{ variant?: 'primary' | 'secondary'; active?: boolean }>`
  padding: 10px 24px;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
  font-family: 'Poppins', 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  background: ${props => {
    if (props.active) return 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(30, 64, 175, 0.3) 100%)';
    return props.variant === 'secondary' 
      ? 'rgba(255,255,255,0.1)' 
      : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)';
  }};
  color: white;
  border: ${props => props.variant === 'secondary' ? '1.5px solid rgba(255,255,255,0.25)' : 'none'};
  box-shadow: ${props => props.variant === 'primary' 
    ? '0 4px 15px rgba(220, 38, 38, 0.3)' 
    : props.active 
    ? '0 4px 15px rgba(59, 130, 246, 0.2)' 
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'primary' 
      ? '0 6px 20px rgba(220, 38, 38, 0.4)' 
      : props.active 
      ? '0 6px 20px rgba(59, 130, 246, 0.3)' 
      : '0 4px 15px rgba(255, 255, 255, 0.15)'};
    background: ${props => {
      if (props.active) return 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(30, 64, 175, 0.4) 100%)';
      return props.variant === 'secondary' 
        ? 'rgba(255,255,255,0.2)' 
        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    }};
    border-color: ${props => props.variant === 'secondary' ? 'rgba(255,255,255,0.4)' : 'transparent'};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 20px;
    text-align: center;
    margin-bottom: 0;
    border-radius: 20px;
    font-size: 0.85rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const GalleryDropdown = styled.div<{ isOpen: boolean }>`
  position: relative;
  display: inline-block;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const GalleryButton = styled.button<{ variant?: 'primary' | 'secondary'; active?: boolean }>`
  padding: 10px 24px;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
  font-family: 'Poppins', 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
  
  background: ${props => {
    if (props.active) return 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(30, 64, 175, 0.3) 100%)';
    return props.variant === 'secondary' 
      ? 'rgba(255,255,255,0.1)' 
      : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)';
  }};
  color: white;
  border: 1.5px solid rgba(255,255,255,0.25);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.15);
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 20px;
    text-align: center;
    margin-bottom: 0;
    justify-content: center;
    border-radius: 20px;
    font-size: 0.85rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const DropdownContent = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2), 0 0 0 1px rgba(0, 0, 0, 0.05);
  min-width: 200px;
  z-index: 1001;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    position: static;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    background: rgba(255,255,255,0.15);
    margin-top: 10px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(15px);
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 14px 20px;
  border: none;
  background: transparent;
  color: #1e40af;
  font-weight: 600;
  font-family: 'Poppins', 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1.4;
  position: relative;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 64, 175, 0.05) 100%);
    color: #1e40af;
    padding-left: 24px;
    transform: translateX(4px);
    
    &::before {
      transform: scaleY(1);
    }
  }
  
  &:active {
    transform: translateX(2px);
  }
  
  &:first-child {
    border-radius: 16px 16px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 16px 16px;
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    color: white;
    padding: 16px 20px;
    font-size: 0.9rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    &::before {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.3) 100%);
    }
    
    &:hover {
      background: rgba(255,255,255,0.2);
      color: white;
      padding-left: 24px;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MainContent = styled.main<{ isAdminPage?: boolean }>`
  flex: 1;
  margin-top: ${props => props.isAdminPage ? '80px' : '0'};
`;

interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAdminButton, setShowAdminButton] = useState(false);
  const [isGalleryDropdownOpen, setIsGalleryDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsGalleryDropdownOpen(false);
    setIsServicesDropdownOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Déterminer si on est sur la page d'administration
  const isAdminPage = location.pathname === '/admin';

  // Timeout d'inactivité global : déconnexion après 30 min d'inactivité
  // Fonctionne même si l'administrateur est sur la page d'accueil ou les galeries
  useEffect(() => {
    const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const lastActivityRef = { current: Date.now() };

    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      // Throttle les écritures
      const storedLast = localStorage.getItem('adminLastActivity');
      if (!storedLast || now - Number(storedLast) > 5000) {
        localStorage.setItem('adminLastActivity', String(now));
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'adminLastActivity' && e.newValue) {
        lastActivityRef.current = Number(e.newValue);
      }
      // Sync logout if another tab logs out
      if (e.key === 'isAuthenticated' && e.newValue === null) {
        if (location.pathname === '/admin') {
          navigate('/');
        }
      }
    };

    const clearAuthAndRedirect = () => {
      localStorage.removeItem('adminUser');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('adminLastActivity');
      if (location.pathname === '/admin') {
        navigate('/');
      }
    };

    // Événements d'activité
    const activityEvents: Array<keyof WindowEventMap> = [
      'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'
    ];

    // Ne suivre l'activité que si l'utilisateur est authentifié
    const checkAuth = () => localStorage.getItem('isAuthenticated') === 'true';

    if (checkAuth()) {
      activityEvents.forEach(evt => window.addEventListener(evt, updateActivity, { passive: true }));
      window.addEventListener('storage', handleStorageChange);
      
      const interval = window.setInterval(() => {
        const now = Date.now();
        const storedLast = Number(localStorage.getItem('adminLastActivity') || 0);
        const last = Math.max(lastActivityRef.current, storedLast);

        if (checkAuth() && now - last > INACTIVITY_TIMEOUT_MS) {
          console.log('⏰ Session expirée globalement (30 min)');
          clearAuthAndRedirect();
        }
      }, 15_000);

      return () => {
        activityEvents.forEach(evt => window.removeEventListener(evt, updateActivity as any));
        window.removeEventListener('storage', handleStorageChange);
        window.clearInterval(interval);
      };
    }
  }, [location.pathname, navigate]);

  // Gérer l'affichage du bouton Administration avec Ctrl + Alt + A
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setShowAdminButton(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'a') {
        // Garder le bouton visible pendant 5 secondes après avoir relâché les touches
        setTimeout(() => {
          setShowAdminButton(false);
        }, 5000);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return (
    <AppContainer>
      {/* Navbar */}
      <Navbar>
        <NavContainer>
          <Logo onClick={() => handleNavigation('/')}>
            <LogoImage src="/logo.jpg" alt="Shotokan Karaté Club Logo" />
            <div>
              <div>Karaté Shotokan</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '-5px' }}>Côte d'Ivoire</div>
            </div>
          </Logo>
          <NavButtons isOpen={isMobileMenuOpen}>
            {!isAdminPage ? (
              // Navigation pour la page d'accueil
              <>
                <NavButton 
                  variant="secondary" 
                  active={isActive('/')}
                  onClick={() => handleNavigation('/')}
                >
                  Accueil
                </NavButton>
                <GalleryDropdown isOpen={isGalleryDropdownOpen}>
                  <GalleryButton 
                    variant="secondary"
                    onClick={() => setIsGalleryDropdownOpen(!isGalleryDropdownOpen)}
                  >
                    Galeries
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ transform: isGalleryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  </GalleryButton>
                  <DropdownContent isOpen={isGalleryDropdownOpen}>
                    <DropdownItem onClick={() => handleNavigation('/photos')}>
                      Photos
                    </DropdownItem>
                    <DropdownItem onClick={() => handleNavigation('/videos')}>
                      Vidéos
                    </DropdownItem>
                  </DropdownContent>
                </GalleryDropdown>

                <GalleryDropdown isOpen={isServicesDropdownOpen}>
                  <GalleryButton
                    variant="secondary"
                    onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                  >
                    Services
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ transform: isServicesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  </GalleryButton>
                  <DropdownContent isOpen={isServicesDropdownOpen}>
                    <DropdownItem onClick={() => handleNavigation('/achat')}>
                      Achat
                    </DropdownItem>
                    <DropdownItem onClick={() => handleNavigation('/vente')}>
                      Vente
                    </DropdownItem>
                  </DropdownContent>
                </GalleryDropdown>
                <NavButton 
                  variant="primary"
                  onClick={() => {
                    window.dispatchEvent(new Event('showClubForm'));
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Affiliation Club
                </NavButton>
                <NavButton 
                  variant="secondary" 
                  onClick={() => {
                    window.dispatchEvent(new Event('showIndividuelForm'));
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Affiliation Individuelle
                </NavButton>
                {showAdminButton && (
                  <NavButton 
                    variant="secondary" 
                    onClick={() => handleNavigation('/admin')}
                  >
                    Administration
                  </NavButton>
                )}
              </>
            ) : (
              // Navigation pour la page d'administration
              <>
                <NavButton 
                  variant="secondary" 
                  onClick={() => handleNavigation('/')}
                >
                  ← Retour à l'accueil
                </NavButton>
                <NavButton 
                  variant="secondary" 
                  active={isActive('/admin')}
                >
                  🔐 Administration
                </NavButton>
              </>
            )}
          </NavButtons>
          
          <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </MobileMenuButton>
        </NavContainer>
      </Navbar>
      
      <MobileOverlay isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(false)} />

      <MainContent isAdminPage={isAdminPage}>
        {children}
      </MainContent>
    </AppContainer>
  );
}

export default Layout;
